<template>
  <div
    v-bind="$attrs"
    class="checklist-table-task"
    :id="`${task.type}-${task.id}`"
    :class="taskClasses"
    data-locator="checklist-task-row"
    @mouseenter="handleMouseEnterTask"
    @mouseleave="handleMouseLeaveTask"
  >
    <template v-if="isEdited">
      <v-skeleton-loader class="checklist-table-task-skeleton" type="list-item-two-line"/>
    </template>
    <template v-else>
      <div class="checklist-table-first-column px-1">
        <ChecklistTableDrag
          :dragged-item="task"
          :draggable="isDraggable"
        />
        <ChecklistTableAddButton
          v-if="isDraggable || isAddMenuOpen"
          data-locator="add-after-task-menu-button"
          icon-only
          :item="task"
          :mnemo="roomMnemo"
          :prev-id="task.id"
          :prev-type="task.type"
          :prev-depth="task.depth"
          @menu-close="isAddMenuOpen = false"
          @menu-open="isAddMenuOpen = true"
        />
        <ChecklistTableSelect
          :selected="isTaskSelected"
          @select="onSelectedTaskClick"
        />
      </div>

      <ChecklistTableTaskTitle
        ref="ChecklistTableTaskTitle"
        :is-hovered="isMouseInTaskRow && !isDragged"
        :task="task"
        :parent="parent"
      />

      <template v-for="header in checklistTableHeader" :key="header">
        <template v-if="checklistDetailsPending">
          <v-skeleton-loader type="text" />
        </template>
        <template v-else-if="getComponentName(header)">
          <component
            :is="getComponentName(header)"
            :mnemo="roomMnemo"
            :item="task"
            :parent="parent"
            v-bind="getBindings(header)"
          />
        </template>
        <template v-else>
          <ChecklistTableTaskEmptyCell />
        </template>
      </template>

      <ChecklistTableTaskMenu
        v-if="!checklistDetailsPending"
        :mnemo="roomMnemo"
        :item="task"
        :parent="parent"
        @edit-task-title="onEditTaskTitle"
      />
    </template>
  </div>

  <ChecklistStatusDialog
      v-if="checklistTableStatusDialogIsOpen"
      :mnemo="roomMnemo"
      :tasks="[task]"
      @close="checklistTableStatusDialogIsOpen = false"
    />
</template>

<script lang="ts">
import { defineComponent, inject, PropType } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import AppTooltip from '@/common/AppTooltip.vue'
import { useTranslation } from '@/common/utils/translation'
import ChecklistStatusDialog from '@/project/checklist/dialogs/ChecklistStatusDialog.vue'
import ChecklistTableDrag from '@/project/checklist/table/ChecklistTableDrag.vue'
import ChecklistTableSelect from '@/project/checklist/table/ChecklistTableSelect.vue'
import ChecklistTableTaskComments from '@/project/checklist/table/ChecklistTableTaskComments.vue'
import ChecklistTableTaskDate from '@/project/checklist/table/ChecklistTableTaskDate.vue'
import ChecklistTableTaskEmptyCell from '@/project/checklist/table/ChecklistTableTaskEmptyCell.vue'
import ChecklistTableTaskMenu from '@/project/checklist/table/ChecklistTableTaskMenu.vue'
import ChecklistTableTaskResponsibles from '@/project/checklist/table/ChecklistTableTaskResponsibles.vue'
import ChecklistTableTaskStatus from '@/project/checklist/table/ChecklistTableTaskStatus.vue'
import ChecklistTableTaskTitle from '@/project/checklist/table/ChecklistTableTaskTitle.vue'
import ChecklistTableTaskValidators from '@/project/checklist/table/ChecklistTableTaskValidators.vue'
import { ChecklistSection, ChecklistTask } from '@/project/checklist/types'
import { ADD_TASK_TO_SELECTION, REMOVE_TASK_FROM_SELECTION } from '@/store/modules/checklist/action_types'

import ChecklistTableAddButton from './ChecklistTableAddButton.vue'

export default defineComponent({
  name: 'ChecklistTableTask',
  components: {
    AppTooltip,
    ChecklistTableSelect,
    ChecklistStatusDialog,
    ChecklistTableAddButton,
    ChecklistTableTaskComments,
    ChecklistTableTaskDate,
    ChecklistTableDrag,
    ChecklistTableTaskEmptyCell,
    ChecklistTableTaskMenu,
    ChecklistTableTaskResponsibles,
    ChecklistTableTaskStatus,
    ChecklistTableTaskTitle,
    ChecklistTableTaskValidators,
  },
  props: {
    isDragged: {
      type: Boolean,
    },
    task: {
      type: Object as PropType<ChecklistTask>,
      required: true,
    },
    parent: {
      type: Object as PropType<ChecklistSection>,
      required: true,
    },
  },
  setup () {
    const checklistTableWidth = inject<number>('checklistTableWidth')
    const { t } = useTranslation({ keyPrefix: 'project.checklist.table.ChecklistTableTaskTitle' })

    return {
      checklistTableWidth,
      t,
    }
  },
  data () {
     return {
      checklistTableStatusDialogIsOpen: false,
      isMouseInTaskRow: false,
      isAddMenuOpen: false,
     }
  },
  computed: {
    ...mapState('checklist', [
      'checklistDetailsPending',
      'checklistDragItem',
      'checklistPane',
      'checklistTableHeader',
      'editTaskPending',
      'taskDetailsPendingIds',
    ]),
    ...mapGetters('checklist', [
      'isChecklistFiltered',
      'selectedTasks',
    ]),
    ...mapGetters('room', ['roomMnemo', 'isCurrentUserPm']),
    isDraggable () {
      return this.isMouseInTaskRow && !this.isChecklistFiltered && this.isCurrentUserPm
    },
    isEdited () {
      return (this.editTaskPending === this.task.id && !this.isOpenedInPane) || this.taskDetailsPendingIds.includes(this.task.id)
    },
    isOpenedInPane () {
      return this.checklistPane.tasks.find((task: ChecklistTask) => task.id === this.task.id)
    },
    isTaskSelected () {
      return this.selectedTasks.some((task: ChecklistTask) => task.id === this.task.id)
    },
    rowWidth () {
      return `${this.checklistTableWidth}px`
    },
    taskClasses () {
      return {
        'checklist-table-task--awaiting-delete-validation': this.task.isAwaitingValidation,
        'checklist-table-task--opened': this.isOpenedInPane,
        'drag-in-progress': this.checklistDragItem,
        'dragged': this.isDragged,
        'hovered': this.isMouseInTaskRow && !this.isDragged,
        'selected': this.isTaskSelected,
      }
    },
  },
  methods: {
    ...mapActions('checklist', [ADD_TASK_TO_SELECTION, REMOVE_TASK_FROM_SELECTION]),
    getComponentName (header: string) {
      const componentMap: Record<string, string> = {
        responsibles: 'ChecklistTableTaskResponsibles',
        validators: 'ChecklistTableTaskValidators',
        date: 'ChecklistTableTaskDate',
        status: 'ChecklistTableTaskStatus',
        comments: 'ChecklistTableTaskComments',
      }

      return componentMap[header] || null
    },
    getBindings(header: string) {
      if (header === "status") {
        return {
          'onEdit-status': () => this.checklistTableStatusDialogIsOpen = true,
        }
      }

      return {}
    },
    onEditTaskTitle () {
      (this.$refs['ChecklistTableTaskTitle'] as InstanceType<typeof ChecklistTableTaskTitle>).enableTitleEdition()
    },
    onSelectedTaskClick () {
      if (this.isTaskSelected) {
        this.REMOVE_TASK_FROM_SELECTION(this.task)
      } else {
        this.ADD_TASK_TO_SELECTION(this.task)
      }
    },
    handleMouseEnterTask () {
      if (this.checklistDragItem) return
      this.isMouseInTaskRow = true
    },
    handleMouseLeaveTask () {
      if (this.checklistDragItem) return
      this.isMouseInTaskRow = false
    },
  },
})
</script>

<style scoped lang="scss">
@import "@/scss/border-tokens";

@import "@/project/checklist/table/checklist-table.scss";

.checklist-table-task-skeleton {
  width: v-bind(rowWidth);
  position: sticky;
  left: 0;
  grid-column: row-start / row-end;
}

.checklist-table-task {
  display: grid;
  grid-template-columns: subgrid;
  grid-column: row-start / row-end;
  border-bottom: get-border();
  border-radius: inherit;
  user-select: none;

  &--opened,
  &:has(.checklist-table-title-input:focus-within) {
    background-color: rgb(var(--v-theme-surface-info));
  }

  &--awaiting-delete-validation {
    pointer-events: none;
    background-color: rgb(var(--v-theme-surface-secondary)) !important;
    color: rgb(var(--v-theme-text-disabled)) !important;
  }

  &.hovered {
    background-color: rgb(var(--v-theme-surface-tertiary));
  }
  &.selected {
    background-color: rgb(var(--v-theme-surface-info));
  }
}

.checklist-table-first-column {
  align-items: start;
  column-gap: 2px;
  display: flex;
  padding-top: 10px;
}

:deep(.app-button.icon-only) {
  margin-top: -4px;
}
</style>
